<template>
  <div class="admin-announcements">
    <div class="page-heading">
      <div>
        <h1>Announcements</h1>
        <p>Create release announcements displayed to GStocky users.</p>
      </div>
      <button class="primary-btn" @click="openCreateForm">
        <AppIcon name="bi-plus-lg" class="bi bi-plus-lg" />
        New announcement
      </button>
    </div>

    <div v-if="showForm" class="announcement-editor">
      <div class="editor-form">
        <div class="editor-title-row">
          <div>
            <span class="eyebrow">{{ editingId ? 'Edit announcement' : 'New announcement' }}</span>
            <h2>Announcement details</h2>
          </div>
          <button class="icon-btn" title="Close" @click="closeForm"><AppIcon name="bi-x-lg" class="bi bi-x-lg" /></button>
        </div>

        <div class="form-grid">
          <label class="field">
            <span>App version *</span>
            <input v-model.trim="form.app_version" type="text" placeholder="e.g. 2.5.0">
          </label>
          <label class="field">
            <span>Publish date *</span>
            <input v-model="form.published_at" type="datetime-local">
          </label>
        </div>

        <div class="language-section">
          <div class="language-label">English (default)</div>
          <label class="field">
            <span>Title *</span>
            <input v-model.trim="form.title" type="text" placeholder="What’s new in GStocky">
          </label>
          <label class="field">
            <span>Message *</span>
            <textarea v-model.trim="form.message" rows="4" placeholder="Tell users what changed and why it matters."></textarea>
          </label>
        </div>

        <details class="translation-panel">
          <summary>French translation</summary>
          <div class="translation-fields">
            <label class="field">
              <span>French title</span>
              <input v-model.trim="form.translations.fr.title" type="text">
            </label>
            <label class="field">
              <span>French message</span>
              <textarea v-model.trim="form.translations.fr.message" rows="3"></textarea>
            </label>
          </div>
        </details>

        <details class="translation-panel">
          <summary>Arabic translation</summary>
          <div class="translation-fields" dir="rtl">
            <label class="field">
              <span>العنوان</span>
              <input v-model.trim="form.translations.ar.title" type="text">
            </label>
            <label class="field">
              <span>الرسالة</span>
              <textarea v-model.trim="form.translations.ar.message" rows="3"></textarea>
            </label>
          </div>
        </details>

        <div class="media-section">
          <div class="form-grid">
            <label class="field">
              <span>Media type</span>
              <select v-model="form.asset_type">
                <option value="">No media</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </label>
            <label class="field">
              <span>Media URL</span>
              <input v-model.trim="form.asset_url" type="url" :disabled="!form.asset_type" placeholder="https://...">
            </label>
          </div>
          <label class="field">
            <span>Media description</span>
            <input v-model.trim="form.asset_alt" type="text" :disabled="!form.asset_type" placeholder="Accessible description of the media">
          </label>
        </div>

        <label class="enabled-control">
          <input v-model="form.enabled" type="checkbox">
          <span>
            <strong>Enabled</strong>
            <small>Users will see it when the publish date is reached.</small>
          </span>
        </label>

        <div class="editor-actions">
          <button class="secondary-btn" @click="closeForm">Cancel</button>
          <button class="primary-btn" :disabled="saving || !canSave" @click="saveAnnouncement">
            {{ saving ? 'Saving...' : (editingId ? 'Save changes' : 'Publish announcement') }}
          </button>
        </div>
      </div>

      <div class="preview-panel">
        <span class="eyebrow">Live preview</span>
        <div class="announcement-preview">
          <div class="preview-media" :class="{ empty: !form.asset_url }">
            <img v-if="form.asset_type === 'image' && form.asset_url" :src="form.asset_url" :alt="form.asset_alt || form.title">
            <video v-else-if="form.asset_type === 'video' && form.asset_url" :src="form.asset_url" controls></video>
            <AppIcon name="bi-megaphone" v-else class="bi bi-megaphone" />
          </div>
          <div class="preview-content">
            <span class="preview-version">GStocky {{ form.app_version || 'version' }}</span>
            <h3>{{ form.title || 'Announcement title' }}</h3>
            <p>{{ form.message || 'Your announcement message will appear here.' }}</p>
            <button>OK, got it</button>
          </div>
        </div>
      </div>
    </div>

    <div class="announcements-list-card">
      <div class="list-heading">
        <h2>Announcement history</h2>
        <span>{{ announcements.length }} total</span>
      </div>

      <div v-if="loading" class="empty-state">Loading announcements...</div>
      <div v-else-if="!announcements.length" class="empty-state">
        <AppIcon name="bi-megaphone" class="bi bi-megaphone" />
        <strong>No announcements yet</strong>
        <span>Create your first announcement to notify users about a release.</span>
      </div>
      <div v-else class="announcement-list">
        <article v-for="announcement in announcements" :key="announcement.id" class="announcement-row">
          <div class="announcement-mark"><AppIcon name="bi-megaphone" class="bi bi-megaphone" /></div>
          <div class="announcement-main">
            <div class="announcement-title-line">
              <strong>{{ announcement.title }}</strong>
              <span class="version-chip">v{{ announcement.app_version }}</span>
              <span class="status-chip" :class="announcementStatus(announcement).className">
                {{ announcementStatus(announcement).label }}
              </span>
            </div>
            <p>{{ announcement.message }}</p>
            <div class="announcement-meta">
              <span><AppIcon name="bi-calendar3" class="bi bi-calendar3" />{{ formatDateTime(announcement.published_at) }}</span>
              <span v-if="announcement.asset_type"><AppIcon name="bi-paperclip" class="bi bi-paperclip" />{{ announcement.asset_type }}</span>
              <span v-if="translationCount(announcement)"><AppIcon name="bi-translate" class="bi bi-translate" />{{ translationCount(announcement) }} translations</span>
            </div>
          </div>
          <div class="row-actions">
            <button class="secondary-btn compact" @click="editAnnouncement(announcement)">Edit</button>
            <label class="switch" :title="announcement.enabled ? 'Disable announcement' : 'Enable announcement'">
              <input type="checkbox" :checked="announcement.enabled" :disabled="updatingId === announcement.id" @change="toggleAnnouncement(announcement, $event.target.checked)">
              <span class="slider"></span>
            </label>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

const emptyForm = () => ({
  app_version: '',
  title: '',
  message: '',
  translations: {
    fr: { title: '', message: '' },
    ar: { title: '', message: '' }
  },
  asset_type: '',
  asset_url: '',
  asset_alt: '',
  enabled: true,
  published_at: moment().format('YYYY-MM-DDTHH:mm')
})

export default {
  name: 'AdminAnnouncements',
  data() {
    return {
      announcements: [],
      loading: true,
      saving: false,
      updatingId: null,
      showForm: false,
      editingId: null,
      form: emptyForm()
    }
  },
  computed: {
    canSave() {
      return Boolean(this.form.app_version && this.form.title && this.form.message && this.form.published_at)
    }
  },
  created() {
    this.fetchAnnouncements()
  },
  methods: {
    async fetchAnnouncements() {
      this.loading = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/admin/announcements`)
        this.announcements = response.data.announcements || []
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to load announcements')
      } finally {
        this.loading = false
      }
    },
    openCreateForm() {
      this.editingId = null
      this.form = emptyForm()
      this.showForm = true
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    closeForm() {
      this.showForm = false
      this.editingId = null
      this.form = emptyForm()
    },
    editAnnouncement(announcement) {
      const translations = announcement.translations || {}
      this.editingId = announcement.id
      this.form = {
        app_version: announcement.app_version,
        title: announcement.title,
        message: announcement.message,
        translations: {
          fr: { title: translations.fr?.title || '', message: translations.fr?.message || '' },
          ar: { title: translations.ar?.title || '', message: translations.ar?.message || '' }
        },
        asset_type: announcement.asset_type || '',
        asset_url: announcement.asset_url || '',
        asset_alt: announcement.asset_alt || '',
        enabled: Boolean(announcement.enabled),
        published_at: moment(announcement.published_at).format('YYYY-MM-DDTHH:mm')
      }
      this.showForm = true
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    payload() {
      return {
        ...this.form,
        published_at: moment(this.form.published_at).toISOString(),
        asset_type: this.form.asset_type || null,
        asset_url: this.form.asset_type ? (this.form.asset_url || null) : null,
        asset_alt: this.form.asset_type ? (this.form.asset_alt || null) : null
      }
    },
    async saveAnnouncement() {
      if (!this.canSave) return
      this.saving = true
      try {
        const baseUrl = `${process.env.VUE_APP_API}/admin/announcements`
        const response = this.editingId
          ? await axios.patch(`${baseUrl}/${this.editingId}`, this.payload())
          : await axios.post(baseUrl, this.payload())

        const saved = response.data.announcement
        const existingIndex = this.announcements.findIndex(item => item.id === saved.id)
        if (existingIndex >= 0) this.$set(this.announcements, existingIndex, saved)
        else this.announcements.unshift(saved)
        this.sortAnnouncements()
        this.closeForm()
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to save announcement')
      } finally {
        this.saving = false
      }
    },
    async toggleAnnouncement(announcement, enabled) {
      this.updatingId = announcement.id
      try {
        const response = await axios.patch(`${process.env.VUE_APP_API}/admin/announcements/${announcement.id}`, { enabled })
        const index = this.announcements.findIndex(item => item.id === announcement.id)
        if (index >= 0) this.$set(this.announcements, index, response.data.announcement)
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to update announcement')
      } finally {
        this.updatingId = null
      }
    },
    sortAnnouncements() {
      this.announcements.sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
    },
    announcementStatus(announcement) {
      if (!announcement.enabled) return { label: 'Disabled', className: 'disabled' }
      if (new Date(announcement.published_at) > new Date()) return { label: 'Scheduled', className: 'scheduled' }
      return { label: 'Published', className: 'published' }
    },
    translationCount(announcement) {
      return Object.keys(announcement.translations || {}).length
    },
    formatDateTime(date) {
      return moment(date).format('MMM D, YYYY · HH:mm')
    }
  }
}
</script>

<style scoped>
.admin-announcements { display: flex; flex-direction: column; gap: 24px; }
.page-heading, .editor-title-row, .list-heading, .announcement-title-line, .announcement-meta, .row-actions, .enabled-control { display: flex; align-items: center; }
.page-heading { justify-content: space-between; gap: 20px; }
.page-heading h1 { color: #111827; font-size: 24px; margin: 0 0 5px; }
.page-heading p, .list-heading span { color: #64748b; font-size: 13px; margin: 0; }
.primary-btn, .secondary-btn, .icon-btn { border: 0; border-radius: 9px; cursor: pointer; font-size: 13px; font-weight: 650; }
.primary-btn { align-items: center; background: #4f46e5; color: #fff; display: inline-flex; gap: 7px; padding: 10px 15px; }
.primary-btn:disabled { cursor: not-allowed; opacity: .55; }
.secondary-btn { background: #fff; border: 1px solid #dbe3ef; color: #475569; padding: 9px 14px; }
.secondary-btn.compact { padding: 7px 11px; }
.icon-btn { align-items: center; background: #f8fafc; color: #64748b; display: flex; height: 34px; justify-content: center; width: 34px; }
.announcement-editor { align-items: start; display: grid; gap: 24px; grid-template-columns: minmax(0, 1.35fr) minmax(300px, .65fr); }
.editor-form, .preview-panel, .announcements-list-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; }
.editor-form { padding: 22px; }
.editor-title-row { justify-content: space-between; margin-bottom: 20px; }
.editor-title-row h2, .list-heading h2 { color: #111827; font-size: 16px; margin: 3px 0 0; }
.eyebrow { color: #6366f1; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.form-grid { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.field { display: flex; flex-direction: column; gap: 7px; margin-bottom: 14px; }
.field span { color: #475569; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.field input, .field textarea, .field select { background: #fff; border: 1px solid #dbe3ef; border-radius: 8px; color: #111827; font: inherit; font-size: 13px; outline: none; padding: 10px 11px; resize: vertical; width: 100%; }
.field input:focus, .field textarea:focus, .field select:focus { border-color: #818cf8; box-shadow: 0 0 0 3px rgba(99, 102, 241, .1); }
.field input:disabled { background: #f8fafc; color: #94a3b8; }
.language-section, .media-section { border-top: 1px solid #eef2f7; margin-top: 5px; padding-top: 18px; }
.language-label { color: #111827; font-size: 12px; font-weight: 750; margin-bottom: 12px; }
.translation-panel { border: 1px solid #e5e7eb; border-radius: 8px; margin-top: 10px; }
.translation-panel summary { color: #475569; cursor: pointer; font-size: 12px; font-weight: 700; padding: 12px 14px; }
.translation-fields { border-top: 1px solid #eef2f7; padding: 14px 14px 0; }
.enabled-control { background: #f8fafc; border-radius: 8px; gap: 10px; margin-top: 16px; padding: 12px; }
.enabled-control input { height: 17px; width: 17px; }
.enabled-control span { display: flex; flex-direction: column; }
.enabled-control strong { color: #334155; font-size: 13px; }
.enabled-control small { color: #64748b; font-size: 11px; }
.editor-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.preview-panel { padding: 20px; position: sticky; top: 88px; }
.announcement-preview { border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: 0 16px 38px rgba(15, 23, 42, .1); margin-top: 12px; overflow: hidden; }
.preview-media { align-items: center; background: linear-gradient(135deg, #eef2ff, #f5f3ff); display: flex; height: 190px; justify-content: center; overflow: hidden; }
.preview-media img, .preview-media video { height: 100%; object-fit: cover; width: 100%; }
.preview-media i { color: #6366f1; font-size: 50px; }
.preview-content { padding: 20px; }
.preview-version { color: #6366f1; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.preview-content h3 { color: #111827; font-size: 19px; margin: 7px 0; }
.preview-content p { color: #64748b; font-size: 13px; line-height: 1.55; margin: 0; white-space: pre-wrap; }
.preview-content button { background: #111827; border: 0; border-radius: 8px; color: #fff; font-size: 12px; margin-top: 18px; padding: 9px 14px; width: 100%; }
.announcements-list-card { overflow: hidden; }
.list-heading { border-bottom: 1px solid #e5e7eb; justify-content: space-between; padding: 17px 20px; }
.empty-state { align-items: center; color: #94a3b8; display: flex; flex-direction: column; font-size: 13px; gap: 7px; padding: 55px 20px; text-align: center; }
.empty-state i { font-size: 32px; }
.empty-state strong { color: #475569; }
.announcement-row { align-items: center; border-bottom: 1px solid #eef2f7; display: flex; gap: 15px; padding: 17px 20px; }
.announcement-row:last-child { border-bottom: 0; }
.announcement-mark { align-items: center; background: #eef2ff; border-radius: 10px; color: #4f46e5; display: flex; flex: 0 0 40px; height: 40px; justify-content: center; }
.announcement-main { flex: 1; min-width: 0; }
.announcement-title-line { flex-wrap: wrap; gap: 7px; }
.announcement-title-line strong { color: #111827; font-size: 14px; }
.version-chip, .status-chip { border-radius: 999px; font-size: 10px; font-weight: 750; padding: 3px 7px; }
.version-chip { background: #f1f5f9; color: #475569; }
.status-chip.published { background: #dcfce7; color: #15803d; }
.status-chip.scheduled { background: #fef3c7; color: #a16207; }
.status-chip.disabled { background: #f1f5f9; color: #64748b; }
.announcement-main p { color: #64748b; display: -webkit-box; font-size: 12px; line-height: 1.45; margin: 6px 0; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.announcement-meta { color: #94a3b8; flex-wrap: wrap; font-size: 10px; gap: 12px; }
.announcement-meta span { align-items: center; display: inline-flex; gap: 4px; }
.row-actions { gap: 12px; }
.switch { display: inline-block; height: 22px; position: relative; width: 40px; }
.switch input { height: 0; opacity: 0; width: 0; }
.slider { background: #cbd5e1; border-radius: 999px; cursor: pointer; inset: 0; position: absolute; transition: .2s; }
.slider::before { background: #fff; border-radius: 50%; bottom: 3px; content: ''; height: 16px; left: 3px; position: absolute; transition: .2s; width: 16px; }
.switch input:checked + .slider { background: #4f46e5; }
.switch input:checked + .slider::before { transform: translateX(18px); }
@media (max-width: 1000px) { .announcement-editor { grid-template-columns: 1fr; } .preview-panel { position: static; } }
@media (max-width: 650px) { .page-heading, .announcement-row { align-items: stretch; flex-direction: column; } .form-grid { grid-template-columns: 1fr; } .row-actions { justify-content: space-between; } .announcement-mark { display: none; } }
</style>
